import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import { GridLeafColumn } from '@mui/x-data-grid/models/gridColumnGrouping';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';

import { theme } from '~/theme';

type PropsType = {
  columns: GridColDef[];
  columnGroupingModel?: GridColumnGroupingModel;
  sx?: SxProps<Theme>;
};

type UseDataGridStylingOutput = PropsType;
export const useDataGridGroupStyling = (
  props: PropsType,
): UseDataGridStylingOutput => {
  const { columns, columnGroupingModel, sx } = props;

  const groupIdsMap = useMemo(
    () =>
      columnGroupingModel?.map((group) => {
        return group.groupId;
      }) || [],
    [columnGroupingModel],
  );

  const getGroupClassNames = useCallback(
    (groupId: string) => {
      const groupIndex = groupIdsMap.indexOf(groupId) + 1;
      return `groupColumn columnGroup-${groupIndex}`;
    },
    [groupIdsMap],
  );

  // Append column group classnames to Group Header Cells
  const newColumnGroupingModel = useMemo(() => {
    return columnGroupingModel?.map((group) => {
      if (!group.headerClassName) {
        return {
          ...group,
          headerClassName: classNames(
            group.headerClassName,
            getGroupClassNames(group.groupId),
          ),
        };
      }

      return group;
    });
  }, [columnGroupingModel, getGroupClassNames]);

  // Append column group classnames to Column Header Cells & Content Cells
  const newColumns = useMemo(() => {
    // Pass if nothing to process
    if (!newColumnGroupingModel || newColumnGroupingModel?.length === 0) {
      return columns;
    }

    const groupClassNamesByColumn = Object.assign(
      {},
      ...newColumnGroupingModel.map((columnGroup) => {
        const groupClassName = getGroupClassNames(columnGroup.groupId);

        const fieldNames = columnGroup.children
          .filter((columnNode) => columnNode.hasOwnProperty('field'))
          .map((columnNode) => (columnNode as GridLeafColumn).field);

        return Object.fromEntries(
          fieldNames.map((fieldName) => [fieldName, groupClassName]),
        );
      }),
    );

    return columns.map((column) => {
      if (!!groupClassNamesByColumn[column.field]) {
        const groupClass = groupClassNamesByColumn[column.field];

        return {
          ...column,
          headerClassName: classNames(column.headerClassName, groupClass),
          cellClassName: classNames(column.cellClassName, groupClass),
        };
      }

      return column;
    });
  }, [columns, newColumnGroupingModel, getGroupClassNames]);

  // Append sx with group column styles
  const newSx = useMemo(() => {
    const hasGroups = groupIdsMap.length > 0;
    // Group styling is needed
    const getFirstAfterGroupChangeSelector = (groupIds: string[]): string => {
      const cellClassNames = ['MuiDataGrid-cell', 'MuiDataGrid-columnHeader'];
      const groupClassNames = groupIds.map(
        (_, index) => `columnGroup-${index + 1}`,
      );

      /** Generate css selector
       *  - First group column after non-group column (Start of column groups)
       *  - First column after last group column of each group (Between groups & end of column groups)
       */
      return `& ${[
        ...cellClassNames.map(
          (cellSelector) =>
            `.${cellSelector}:not(.groupColumn) + .${cellSelector}.groupColumn`,
        ),
        ...groupClassNames.map((groupSelector) => [
          ...cellClassNames.map(
            (cellSelector) =>
              `.${cellSelector}.${groupSelector} + .${cellSelector}:not(.${groupSelector})`,
          ),
        ]),
      ].join(', ')}`;
    };

    const groupStyling = hasGroups
      ? {
          [getFirstAfterGroupChangeSelector(groupIdsMap)]: {
            borderLeft: `solid 1px rgba(0,0,0,0.12)`,
          },
        }
      : {};

    // Styling edge-case not handled by library
    const styleFixes = {
      '& .MuiDataGrid-row--detailPanelExpanded.MuiDataGrid-row--lastVisible': {
        '& .MuiDataGrid-cell': {
          borderBottomColor: theme.palette.grey['300'],
        },
      },
    };

    return { ...styleFixes, ...groupStyling, ...sx };
  }, [groupIdsMap, sx]);

  return {
    columns: newColumns,
    columnGroupingModel: newColumnGroupingModel,
    sx: newSx,
  };
};
